import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import {
    initialValuesForm,
    initialValuesValidationCode,
    validationSchemaForm,
    validationSchemaValidationCode,
} from './formData';
import { SarlaftSecurityProps } from './props';
import { SarlaftSecurity } from '../../../../../../shared/types/modules/sarlaft';
import { asyncGetParams } from '../../../../../../services/param.service';
import { ISelect, ParamEnum } from '../../../../../../shared/types/shared';
import dayjs from 'dayjs';
import {
    asyncGetSendCode,
    asyncGetValidateCode,
} from '../../../../../../services/sarlaft.service';
import { ToastSwal } from '../../../../../components/general/toastSwals/toastSwal';

export const useSarlaftSecurity = ({
    data,
    onChangeData,
    onChangeFormik,
    isSuccessfulValidation,
    account,
}: SarlaftSecurityProps) => {
    const [filtersValidation, setFiltersValidation] = useState<ISelect[]>([]);
    const [statusValidation, setStatusValidation] = useState<ISelect[]>([]);
    const [isLoadingSend, setIsLoadingSend] = useState<boolean>(false);
    const [isForwardDisabled, setIsForwardDisabled] = useState(false);
    const [timeLeftForward, setTimeLeftForward] = useState(0);

    const nameAccount = account?.lastName
        ? `${account?.name} ${account?.lastName}`
        : account?.name;
    const onSubmit = (data: SarlaftSecurity) => {
        if (onChangeData) onChangeData(data || {});
    };

    const formik = useFormik({
        initialValues: initialValuesForm,
        validationSchema: validationSchemaForm(!!data?.verificationCode),
        onSubmit,
    });

    const formikValidationCode = useFormik({
        initialValues: initialValuesValidationCode,
        validationSchema: validationSchemaValidationCode,
        onSubmit: async (formValidation) => {
            const phone = account?.cellPhone ?? account?.phone;
            asyncGetValidateCode(
                `${phone};${nameAccount}<${account?.email}>` || '',
                formValidation.verificationCode || ''
            ).then((res) => {
                formik?.setFieldValue('validationCode', res?.status || '');
                formik?.setFieldValue(
                    'verificationCode',
                    formValidation.verificationCode || ''
                );
            });
        },
    });

    useEffect(() => {
        if (onChangeFormik) onChangeFormik(formik);
    }, [formik.values, formik.errors]);

    useEffect(() => {
        formik?.setFieldValue('birthDateSise', data?.birthDateSise || '');
        formik?.setFieldValue('birthDateClient', data?.birthDateClient || '');
        formik?.setFieldValue('filterBirthDate', data?.filterBirthDate || '');
        formik?.setFieldValue('ageSise', data?.ageSise || '');
        formik?.setFieldValue('ageClient', data?.ageClient || '');
        formik?.setFieldValue('filterAge', data?.filterAge || '');
        formik?.setFieldValue('statusValidation', data?.statusValidation || '');
        formik?.setFieldValue('observation', data?.observation || '');
        formik?.setFieldValue('emailAccount', account?.email || '');
    }, [data, account]);

    useEffect(() => {
        const diff = dayjs().diff(dayjs(formik.values.birthDateSise), 'years');
        const birthDateClient = formik.values.birthDateClient;
        const birthDateSise = formik.values.birthDateSise;

        if (birthDateClient === birthDateSise) {
            const state = filtersValidation.find(
                (item) =>
                    item.code ===
                    `${ParamEnum.SARLAFT_FILTER_VALIDATION}_APPROVED`
            );
            formik.setFieldValue('filterBirthDate', state?.id);
        } else if (
            birthDateClient !== birthDateSise &&
            birthDateClient !== ''
        ) {
            const state = filtersValidation.find(
                (item) =>
                    item.code ===
                    `${ParamEnum.SARLAFT_FILTER_VALIDATION}_NOT_APPROVED`
            );
            formik.setFieldValue('filterBirthDate', state?.id);
        } else formik.setFieldValue('filterBirthDate', '');

        if (!isNaN(diff))
            formik.setFieldValue(
                'ageSise',
                diff > 0 ? Math.floor(diff).toString() : '0'
            );
    }, [formik.values.birthDateSise, formik.values.birthDateClient]);

    useEffect(() => {
        const ageClient = formik.values.ageClient;
        const ageSise = formik.values.ageSise;
        if (ageClient === ageSise) {
            const state = filtersValidation.find(
                (item) =>
                    item.code ===
                    `${ParamEnum.SARLAFT_FILTER_VALIDATION}_APPROVED`
            );
            formik.setFieldValue('filterAge', state?.id);
        } else if (ageClient !== ageSise && ageClient !== '') {
            const state = filtersValidation.find(
                (item) =>
                    item.code ===
                    `${ParamEnum.SARLAFT_FILTER_VALIDATION}_NOT_APPROVED`
            );
            formik.setFieldValue('filterAge', state?.id);
        } else formik.setFieldValue('filterAge', '');
    }, [formik.values.ageClient, formik.values.ageSise]);

    useEffect(() => {
        asyncGetParams(ParamEnum.SARLAFT_FILTER_VALIDATION).then((items) =>
            setFiltersValidation(items)
        );

        asyncGetParams(ParamEnum.SARLAFT_STATUS_VALIDATION).then((items) =>
            setStatusValidation(items)
        );
    }, []);

    const handleClickSend = () => {
        setIsLoadingSend(true);
        const phone = account?.cellPhone ?? account?.phone;

        asyncGetSendCode(`${phone};${nameAccount}<${account?.email}>`)
            .then((res) => {
                if (res.sms || res.email) {
                    let hasSuccessfulItem = false;
                    if (
                        res.email?.statusCode === 200 ||
                        res.sms.statusCode === 200
                    ) {
                        hasSuccessfulItem = true;
                        if (!formik.values.deliveryId) {
                            const deliveryId =
                                res.email?.deliveryId ?? res.sms.deliveryId;
                            formik?.setFieldValue(
                                'deliveryId',
                                deliveryId || ''
                            );
                        }
                    }
                    if (hasSuccessfulItem) {
                        setIsForwardDisabled(true);
                        setTimeLeftForward(480);
                        setTimeout(() => {
                            setIsForwardDisabled(false);
                        }, 480000);

                        ToastSwal.fire({
                            text: `Código OTP enviado correctamente`,
                            icon: 'success',
                            timer: 5000,
                            position: 'center-end',
                        });
                    }
                } else {
                    ToastSwal.fire({
                        text: `Lo sentimos, no se pudo enviar el código OTP. Por favor intenta de nuevo.`,
                        icon: 'error',
                        timer: 5000,
                        position: 'center-end',
                    });
                }
            })
            .finally(() => setIsLoadingSend(false));
    };

    useEffect(() => {
        setIsLoadingSend(false);
        if (timeLeftForward > 0) {
            const timer = setInterval(() => {
                setTimeLeftForward((prevTime) => prevTime - 1);
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [timeLeftForward]);

    return {
        formik,
        formikValidationCode,
        filtersValidation,
        statusValidation,
        isSuccessfulValidation,
        isLoadingSend,
        isForwardDisabled,
        timeLeftForward,
        handleClickSend,
    };
};
